import { Injectable } from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {filter} from 'rxjs/operators';

/**
 * AppEventsService - Centralized event management for the application
 * 
 * Scroll Event Usage:
 * 1. Dispatch scroll events: this.appEventSv.handleScroll(container, 'unique-identifier')
 * 2. Subscribe to specific scroll events: this.appEventSv.onScrollEvent('unique-identifier')
 * 3. Subscribe to all scroll events: this.appEventSv.onAllScrollEvents()
 * 
 * Example:
 * // In parent component:
 * this.appEventSv.handleScroll(this.scrollContainer.nativeElement, 'dashboard-main');
 * 
 * // In child component:
 * this.appEventSv.onScrollEvent('dashboard-main').subscribe(event => {
 *   // Handle scroll event from specific container
 * });
 */
@Injectable({
  providedIn: 'root'
})

export class AppEventsService {

  private eventBroker = new Subject<AppEvent<any>>();

  constructor() { }

  on(eventType: AppEventType): Observable<AppEvent<any>> {
    return this.eventBroker.pipe(filter((event) => event.type === eventType));
  }

  // Subscribe to specific scroll events by identifier
  onScrollEvent(identifier: string): Observable<ScrollEvent> {
    return this.eventBroker.pipe(
      filter((event) => event.type === AppEventType.ScrollEvent && event.payload.identifier === identifier)
    ) as Observable<ScrollEvent>;
  }

  // Subscribe to all scroll events (useful for debugging or global scroll handling)
  onAllScrollEvents(): Observable<ScrollEvent> {
    return this.eventBroker.pipe(
      filter((event) => event.type === AppEventType.ScrollEvent)
    ) as Observable<ScrollEvent>;
  }

  dispatch<T>(event: AppEvent<T>): void {
    this.eventBroker.next(event);
  }

  // Handle scroll event with identifier
  handleScroll(container: HTMLDivElement, identifier: string): void {
    if (container) {
      this.dispatch(new ScrollEvent(container, identifier));
    }
  }
}

export enum AppEventType {
  ScrollEvent = 'ScrollEvent'
}

export class AppEvent<T> {

  public type: AppEventType;
  public payload: T;

  constructor(type: AppEventType, payload: T) {
    this.type = type;
    this.payload = payload;
  }
}

export class ScrollEvent extends AppEvent<{container: HTMLDivElement, identifier: string}> {

  constructor(scrollDiv: HTMLDivElement, identifier: string) {
    super(AppEventType.ScrollEvent, {container: scrollDiv, identifier: identifier});
  }

  get container(): HTMLDivElement {
    return this.payload.container;
  }

  get identifier(): string {
    return this.payload.identifier;
  }
}
